import { useCallback, useEffect, useState } from "react";

import { useToken } from "@hightouch/htui";

/**
 * Returns `true` when the viewport is at or above the given theme breakpoint.
 *
 * Resolves the breakpoint from the Chakra theme rather than hardcoding pixels,
 * and seeds from `window.innerWidth` so the first render is already correct
 * (no flicker).
 */
export function useIsScreenAtLeast(
  breakpoint: "sm" | "md" | "lg" | "xl",
): boolean {
  const minWidth = Number(
    useToken("breakpoints", breakpoint).replace("px", ""),
  );

  const [isAtLeast, setIsAtLeast] = useState(
    () => window.innerWidth >= minWidth,
  );

  const handleResize = useCallback(() => {
    setIsAtLeast(window.innerWidth >= minWidth);
  }, [minWidth]);

  useEffect(() => {
    // Re-evaluate on mount too: the token can resolve after the initial state.
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  return isAtLeast;
}

/** Returns `true` when the viewport is at or above the `lg` breakpoint. */
export function useIsLargeScreen(): boolean {
  return useIsScreenAtLeast("lg");
}
